import { UserAccountingPlanValue } from './UserAccountingPlanValue';

/**
 * Profile prepared by an administrator when inviting someone in the company.
 * It is stored on the invitation and applied by the server when the person
 * accepts, whether they create an account or already have one.
 *
 * Every field is optional and `null` means "the administrator did not fill it":
 * the field is left untouched and whatever the person may already have is kept.
 * Any other value overwrites what the person has, so an untouched field must
 * never be sent as an empty string or as `false` - see `buildPrefillPayload`.
 */
export class UserPrefill {
    FirstName: string;
    LastName: string;
    PhoneNumber: string;
    ContactEmail: string;
    LanguageCode: string;
    MileageRatio: number;
    DistanceHomeToWork: number;
    Role: string;
    ManagerId: number;
    EmployeeCategoryId: number;
    Identifier: string;
    OrganizationUnit: string;
    OtherInfo1: string;
    OtherInfo2: string;
    OtherInfo3: string;
    OtherInfo4: string;
    ThirdPartyAccount: string;
    HasRestaurantTicket: boolean;
    HasPerDiem: boolean;
    HasProfessionalCard: boolean;
    /**
     * An empty array clears the values the person may have, `null` leaves them
     * untouched.
     */
    AccountingPlanValues: UserAccountingPlanValue[];

    constructor(model?: UserPrefill) {
        if (model) {
            this.FirstName = model.FirstName;
            this.LastName = model.LastName;
            this.PhoneNumber = model.PhoneNumber;
            this.ContactEmail = model.ContactEmail;
            this.LanguageCode = model.LanguageCode;
            this.MileageRatio = model.MileageRatio;
            this.DistanceHomeToWork = model.DistanceHomeToWork;
            this.Role = model.Role;
            this.ManagerId = model.ManagerId;
            this.EmployeeCategoryId = model.EmployeeCategoryId;
            this.Identifier = model.Identifier;
            this.OrganizationUnit = model.OrganizationUnit;
            this.OtherInfo1 = model.OtherInfo1;
            this.OtherInfo2 = model.OtherInfo2;
            this.OtherInfo3 = model.OtherInfo3;
            this.OtherInfo4 = model.OtherInfo4;
            this.ThirdPartyAccount = model.ThirdPartyAccount;
            this.HasRestaurantTicket = model.HasRestaurantTicket;
            this.HasPerDiem = model.HasPerDiem;
            this.HasProfessionalCard = model.HasProfessionalCard;
            this.AccountingPlanValues = model.AccountingPlanValues
                ? model.AccountingPlanValues.map(
                      (apv) => new UserAccountingPlanValue(apv),
                  )
                : null;
        } else {
            this.FirstName = null;
            this.LastName = null;
            this.PhoneNumber = null;
            this.ContactEmail = null;
            this.LanguageCode = null;
            this.MileageRatio = null;
            this.DistanceHomeToWork = null;
            this.Role = null;
            this.ManagerId = null;
            this.EmployeeCategoryId = null;
            this.Identifier = null;
            this.OrganizationUnit = null;
            this.OtherInfo1 = null;
            this.OtherInfo2 = null;
            this.OtherInfo3 = null;
            this.OtherInfo4 = null;
            this.ThirdPartyAccount = null;
            this.HasRestaurantTicket = null;
            this.HasPerDiem = null;
            this.HasProfessionalCard = null;
            this.AccountingPlanValues = null;
        }
    }
}

/**
 * Turn a prefill into the payload expected by the API, dropping everything the
 * administrator did not fill so that the invited person keeps what they have.
 * Returns null when nothing was filled at all.
 */
export function buildPrefillPayload(
    prefill: UserPrefill,
): Record<string, unknown> {
    if (!prefill) {
        return null;
    }

    const payload: Record<string, unknown> = {};

    for (const field of Object.keys(prefill)) {
        const value = prefill[field];

        // An empty string is what an untouched text input gives us, it means
        // "not filled" and not "erase the current value".
        if (value === null || value === undefined || value === '') {
            continue;
        }

        payload[field] = value;
    }

    return Object.keys(payload).length > 0 ? payload : null;
}
